import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  HeadContent,
  Link,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useRouter,
} from "@tanstack/react-router";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import ScrollToTop from "@/components/ScrollToTop";
import { reportVibeError } from "@/lib/vibe-error-reporting";
import appCss from "../styles.css?url";

const siteTitle =
  "Liquid Screed Exeter | Fast Curing & Free Quotes | Exeter Flow Screed";
const siteDescription =
  "Cemfloor certified liquid screed installers in Exeter & Devon. Fast-curing, level floors for underfloor heating & renovations. Get your free fixed quote today!";
const siteImage =
  "https://assets.cdn.filesafe.space/iehMW4z3GZgoPjqmxz5p/media/6a7469d1e76789340f9e87b3.webp";
const siteUrl = "https://exeterflowscreed.co.uk/";

const localBusinessJsonLd = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "Exeter Flow Screeds",
  image: siteImage,
  "@id": siteUrl,
  url: siteUrl,
  telephone: "07765 712609, 07470 861869",
  email: "efscreed@gmail.com",
  priceRange: "££",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Turner Cl, Newton Poppleford",
    addressLocality: "Sidmouth",
    addressRegion: "Devon",
    postalCode: "EX10 0EN",
    addressCountry: "GB",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 50.6852,
    longitude: -3.2789,
  },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ],
    opens: "07:00",
    closes: "18:00",
  },
  areaServed: [
    "Exeter",
    "Devon",
    "Exmouth",
    "Torquay",
    "Newton Abbot",
    "Tiverton",
    "Honiton",
    "Taunton",
    "Somerset",
    "Plymouth",
    "Barnstaple",
    "Bath",
    "Bournemouth",
    "West Dorset",
  ].map((name) => ({ "@type": "AdministrativeArea", name })),
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Cemfloor Liquid Screed Systems",
    itemListElement: [
      [
        "Underfloor Heating Screed",
        "Cemfloor Therm liquid screed for underfloor heating. High thermal response, warms rooms faster, lower energy bills.",
      ],
      [
        "Heavy-Duty High-Strength Screed",
        "Cemfloor C30 high-strength liquid screed for garages, workshops and commercial floors.",
      ],
      [
        "Ready-To-Tile Screed",
        "Cemfloor Non-Sanding liquid screed. No sanding needed before tiling or laying vinyl.",
      ],
      [
        "Rapid-Drying Screed",
        "Fast-drying Cemfloor liquid screed for tight deadlines. Ready for floor coverings in 10 to 14 days.",
      ],
    ].map(([name, description]) => ({
      "@type": "Offer",
      itemOffered: { "@type": "Service", name, description },
    })),
  },
  sameAs: [
    "https://maps.app.goo.gl/qn4SnRJYtENB7jrE6",
    "https://www.facebook.com/profile.php?id=61578220532150",
  ],
});

const googleAnalyticsScript = `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag("js", new Date());
gtag("config", "G-YTGXFJNFD5", { anonymize_ip: true });
(function(){
  function loadGtag(){var s=document.createElement("script");s.async=true;s.src="https://www.googletagmanager.com/gtag/js?id=G-YTGXFJNFD5";document.head.appendChild(s);}
  var schedule=function(){var ric=window.requestIdleCallback||function(cb){return setTimeout(cb,200);};ric(loadGtag);};
  if(document.readyState==="complete"){schedule();}else{window.addEventListener("load",schedule);}
})();`;

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en-GB" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ScrollToTop />
        <Outlet />
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

function RootErrorComponent({
  error,
  reset,
}: {
  error: Error;
  reset: () => void;
}) {
  const router = useRouter();

  useEffect(() => {
    console.error(error);
    reportVibeError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <section className="w-full max-w-lg rounded-xl border border-border bg-card p-8 text-center shadow-sm">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">
          Something went wrong
        </p>
        <h1 className="mt-3 text-3xl font-bold">This page didn&apos;t load</h1>
        <p className="mt-3 text-muted-foreground">
          Please try again or return to the home page.
        </p>
        <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
          <button
            className="rounded-md bg-primary px-5 py-3 font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            onClick={() => {
              router.invalidate();
              reset();
            }}
            type="button"
          >
            Try again
          </button>
          <Link
            className="rounded-md border border-border px-5 py-3 font-semibold transition-colors hover:bg-muted"
            to="/"
          >
            Go home
          </Link>
        </div>
      </section>
    </main>
  );
}

function RootNotFoundComponent() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-muted px-6 text-foreground">
      <section className="text-center">
        <h1 className="mb-4 text-4xl font-bold">404</h1>
        <p className="mb-6 text-xl text-muted-foreground">
          Oops! Page not found
        </p>
        <Link className="text-primary underline hover:opacity-90" to="/">
          Return to Home
        </Link>
      </section>
    </main>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()(
  {
    head: () => ({
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1.0" },
        { name: "robots", content: "index, follow" },
        { name: "theme-color", content: "#080810" },
        { property: "og:locale", content: "en_GB" },
        { title: siteTitle },
        { name: "description", content: siteDescription },
        {
          name: "keywords",
          content:
            "Cemfloor screed Exeter, Cemfloor Therm Devon, liquid screed Exeter, Cemfloor C30 screed, non-sanding screed Devon, fast drying screed Exeter, underfloor heating screed Devon, Cemfloor certified installer Devon, liquid floor screeding Exeter",
        },
        { name: "author", content: "Exeter Flow Screeds Ltd." },
        { name: "geo.region", content: "GB-DEV" },
        { name: "geo.placename", content: "Exeter & Devon" },
        { property: "og:title", content: siteTitle },
        { property: "og:description", content: siteDescription },
        { property: "og:type", content: "website" },
        { property: "og:image", content: siteImage },
        { property: "og:url", content: siteUrl },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: siteTitle },
        { name: "twitter:description", content: siteDescription },
        { name: "twitter:image", content: siteImage },
      ],
      links: [
        { rel: "stylesheet", href: appCss },
        {
          rel: "icon",
          type: "image/webp",
          href: "https://assets.cdn.filesafe.space/iehMW4z3GZgoPjqmxz5p/media/6ac51749dca4eec5060d1832.webp",
        },
        // LCP image preload — the hero background is the LCP element. Preload
        // it with fetchpriority=high so the browser starts the request as soon
        // as the HTML arrives, before the CSS/JS have parsed.
        {
          rel: "preload",
          as: "image",
          fetchPriority: "high",
          href: "https://assets.cdn.filesafe.space/iehMW4z3GZgoPjqmxz5p/media/6ab280c8880214b00ee9151f.webp",
        },
        // Only preconnect the origins actually used on first paint.
        {
          rel: "preconnect",
          href: "https://assets.cdn.filesafe.space",
          crossOrigin: "anonymous",
        },
        { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
        { rel: "sitemap", type: "application/xml", href: "/sitemap.xml" },
        { rel: "canonical", href: siteUrl },
      ],
      scripts: [
        { type: "application/ld+json", children: localBusinessJsonLd },
        { children: googleAnalyticsScript },
      ],
    }),
    shellComponent: RootShell,
    component: RootComponent,
    notFoundComponent: RootNotFoundComponent,
    errorComponent: RootErrorComponent,
  },
);
